import m from 'mithril';
import dayjs from 'dayjs';
import './Calendar.scss';

interface Attrs {
    year:number,
    month:number,
    titleVisible:boolean,
    selectionStartDate?: string|null,
    selectionEndDate?: string|null,
    minDate?: string,
    maxDate?: string,
    exclusive?: boolean,
    maxRange: number,
    isOnDesktop?: boolean,
    onDateClicked : (date:string) => void
}

class DayCell {
    date: string;
    day: string;
    selectable: boolean;
    isInCurrentMonth: boolean;

    constructor(date:string, day:string, isInCurrentMonth:boolean, selectable:boolean) {
        this.date = date;
        this.day = day;
        this.selectable = selectable;
        this.isInCurrentMonth = isInCurrentMonth;
    }
}

class Week {
    days: DayCell[]
    constructor(days:DayCell[]) {
        this.days = days;
    }
}

export function SingleMonthView() : m.Component<Attrs> {
    let selectionEndDateCandidate:string|null = null;

    return {
        view(vnode) {
            const {year, month, titleVisible, selectionStartDate, selectionEndDate, minDate, maxDate, exclusive, maxRange, isOnDesktop, onDateClicked} = vnode.attrs;        
            let monthIndex = month - 1;     
            
            let monthStartDate = new Date(year, monthIndex, 1);
            if(year < dayjs().year()) {
                let currYear = dayjs().year();
                monthStartDate = new Date(currYear, monthIndex, 1);
            }
            
            let anchorDayjsObj = dayjs(monthStartDate);

            let currMonthStartDate = anchorDayjsObj.startOf("month").format("YYYY-MM-DD");
            let currMonthEndDate = anchorDayjsObj.endOf("month").format("YYYY-MM-DD");

            let calStartDayJsObj = anchorDayjsObj.startOf("month").startOf("week");
            let currViewStartDate = calStartDayJsObj.format("YYYY-MM-DD");

            let currMonthStartDayjsObj = dayjs(currMonthStartDate);
            let currMonthEndDayjsObj = dayjs(currMonthEndDate);

            let monthYearLabel = currMonthStartDayjsObj.format("MMMM, YYYY");
            let dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

            let numWeeks = 6;
            let numDaysInWeek = 7;

            let weeks: Week[] = new Array(numWeeks);

            for(let i:number =0; i< numWeeks; i++) {
                let days:DayCell[] = [];
                for(let j:number = 0; j < numDaysInWeek; j++) {
                    let dateDayjsObj = dayjs(currViewStartDate).add(i*numDaysInWeek + j, 'day');
                    let date:string = dateDayjsObj.format("YYYY-MM-DD");
                    let day:string = dateDayjsObj.format("D");

                    let isInCurrentMonth:boolean = (dateDayjsObj.isSame(currMonthStartDayjsObj) || dateDayjsObj.isAfter(currMonthStartDayjsObj)) && 
                        (dateDayjsObj.isSame(currMonthEndDayjsObj) || dateDayjsObj.isBefore(currMonthEndDayjsObj));

                    days.push(new DayCell(date, day, isInCurrentMonth, true));
                }
                
                let week:Week = new Week(days);
                weeks.push(week);
            }

            return m('div.calendar', { id: "year-month-" + year + "-" + month}, [     
                
                    (titleVisible)?  
                    m("div", {class: "text-center text-bold"}, monthYearLabel) : null,

                    m("table", {class: isOnDesktop? "table calender-table-responsive table-borderless mt-1" : "table table-borderless mt-1"}, 
                        [
                            m("tr", 
                                dayLabels.map(label => 
                                    m("td", {
                                        class : "td-sm text-center text-muted"                                        
                                    }, label))
                            ),                            
                            weeks.map(week => {
                                return m("tr", 
                                    week.days.map(dayCell => {
                                        let isSelectable = true;
                                        if(minDate!=undefined && dayjs(dayCell.date).isBefore(minDate)) {
                                            isSelectable = false;
                                        }
                                        if(maxDate!=undefined && dayjs(dayCell.date).isAfter(maxDate)) {
                                            isSelectable = false;
                                        }

                                        let isSelectionStart = (dayCell.date == selectionStartDate);
                                        let isSelectionEnd = (dayCell.date == selectionEndDate);
                                        let isSelectionInBetween = (selectionStartDate && selectionEndDate && 
                                            dayjs(dayCell.date).isAfter(selectionStartDate) && 
                                            dayjs(dayCell.date).isBefore(selectionEndDate));    
                                        
                                        let isSelectionStartCandidate = (isSelectable && (!selectionStartDate)) || 
                                            (isSelectable && selectionStartDate && dayjs(dayCell.date).isBefore(selectionStartDate)) ||
                                            (isSelectable && selectionStartDate && dayjs(dayCell.date).diff(selectionStartDate, 'day') > maxRange) ||
                                            (isSelectable && selectionStartDate && selectionEndDate);    

                                        let isSelectionEndCandidate = isSelectable 
                                            && selectionStartDate 
                                            && !(selectionEndDate)
                                            && dayjs(dayCell.date).isAfter(selectionStartDate)
                                            && dayjs(dayCell.date).diff(selectionStartDate, 'day') <= maxRange;

                                        let isSelectionInBetweenCandidate = isSelectable 
                                            && selectionStartDate 
                                            && (!selectionEndDate)
                                            && selectionEndDateCandidate
                                            && dayjs(dayCell.date).isAfter(selectionStartDate) 
                                            && dayjs(dayCell.date).diff(selectionStartDate, 'day') < 13
                                            && dayjs(dayCell.date).isBefore(selectionEndDateCandidate);  
                                                                                    

                                        let className = "calendar-cell text-center";
                                        if(isSelectable) {
                                            className = className + " " + "calendar-cell-selectable"
                                        } else {
                                            className = className + " " + "calendar-cell-disabled"
                                        }
                                        
                                        if(isSelectionStart) {
                                            className = className + " " + "selection-start"                                           
                                        } else if(isSelectionEnd) {
                                            className = className + " " + "selection-end"
                                        } else if(isSelectionInBetween) {
                                            className = className + " " + "selection-in-between"
                                        } else if(isSelectionEndCandidate) {
                                            className = className + " " + "selection-end-candidate"
                                        } else if(isSelectionStartCandidate) {
                                            className = className + " " + "selection-start-candidate"
                                        } 
                                        
                                        if(isSelectionInBetweenCandidate) {
                                            className = className + " " + "selection-in-between-candidate"
                                        }                                       

                                        if(dayCell.isInCurrentMonth == false) {
                                            className = className + " " + "calendar-cell-muted"
                                        }
                                        //let isInCurrentMonth 

                                        if(exclusive && !dayCell.isInCurrentMonth) {
                                            return m("td");
                                        } else {
                                            return m("td", {
                                                class: className,
                                                onclick: function(event) {
                                                    event.preventDefault();
                                                    if(isSelectable) {
                                                        onDateClicked(dayCell.date);
                                                    }
                                                    selectionEndDateCandidate = null;
                                                },
                                                
                                                onmouseover: function(event) {
                                                    //console.log("mouse over", dayCell.date)
                                                    if(isSelectable && selectionStartDate) {

                                                        if(dayjs(dayCell.date).diff(selectionStartDate, 'day') <= maxRange) {
                                                            selectionEndDateCandidate = dayCell.date;
                                                        } else {
                                                            selectionEndDateCandidate = null;
                                                        }                                                        
                                                        m.redraw();
                                                    }                                                     
                                                }
                                            },                                            
                                                m("div", {}, dayCell.day) 
                                            )
                                        }
                                    })
                                )
                            })
                        ]                    
                    )
                ]
            );
        }
    }
}

